import { useRef, useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { superadminApi, type UploadedLegalDto, type PlatformLegalStatusDto } from '@/api/superadmin'
import { Button } from '@/components/ui/button'
import { Link } from 'react-router-dom'
import { FileText, Upload, ExternalLink, CheckCircle2, AlertCircle, Pencil } from 'lucide-react'

const apiBase = import.meta.env.VITE_API_URL ?? ''

type DocKey = 'privacyPolicy' | 'terms'

const IN_APP_DOCS = [
  { key: 'data-processing', label: 'Data Processing Terms', description: 'The processor terms customers accept before switching on identity photos.', publicPath: '/legal/data-processing' },
  { key: 'photo-guide', label: 'Identity photos: guide for customers', description: 'Practical guidance and suggested notice wording for customers using photos.', publicPath: '/legal/photo-guide' },
]

const DOCS: { key: DocKey; label: string; description: string; viewUrl: string; apiKey: 'terms' | 'privacy-policy'; uploadFn: (f: File) => Promise<{ data: UploadedLegalDto }> }[] = [
  {
    key: 'privacyPolicy',
    apiKey: 'privacy-policy',
    label: 'Privacy Policy',
    description: 'Describes how you collect, use and protect user data.',
    viewUrl: `${apiBase}/api/v1/legal/privacy-policy`,
    uploadFn: (f) => superadminApi.uploadLegalDocument('privacy-policy', f),
  },
  {
    key: 'terms',
    apiKey: 'terms',
    label: 'Terms & Conditions',
    description: 'The contractual terms users agree to on sign-up.',
    viewUrl: `${apiBase}/api/v1/legal/terms`,
    uploadFn: (f) => superadminApi.uploadLegalDocument('terms', f),
  },
]

function DocCard({ doc, exists, version }: { doc: typeof DOCS[number]; exists: boolean; version?: PlatformLegalStatusDto }) {
  const queryClient = useQueryClient()
  const fileRef = useRef<HTMLInputElement>(null)
  const [error, setError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const mutation = useMutation({
    mutationFn: (file: File) => doc.uploadFn(file),
    onSuccess: (res) => {
      setError(null)
      const r = res.data
      setNotice(r.isNewVersion ? `Saved as version ${r.version}.` : `Identical to the live version (v${r.version}) — no new version created.`)
      queryClient.invalidateQueries({ queryKey: ['legal-status'] })
      queryClient.invalidateQueries({ queryKey: ['legal-versions'] })
    },
    onError: () => { setNotice(null); setError('Upload failed. Make sure the file is a valid PDF.') },
  })

  return (
    <div className="bg-white border border-gray-200 rounded-xl p-6 space-y-4">
      <div className="flex items-start justify-between">
        <div className="flex items-center gap-3">
          <div className={`p-2 rounded-lg ${exists ? 'bg-green-50' : 'bg-gray-50'}`}>
            <FileText size={20} className={exists ? 'text-green-600' : 'text-gray-400'} />
          </div>
          <div>
            <p className="text-sm font-semibold text-gray-900">{doc.label}</p>
            <p className="text-xs text-gray-500 mt-0.5">{doc.description}</p>
          </div>
        </div>
        {exists
          ? <span className="inline-flex items-center gap-1 text-xs text-green-700 bg-green-50 border border-green-200 rounded-full px-2.5 py-0.5">
              <CheckCircle2 size={11} /> Uploaded
            </span>
          : <span className="inline-flex items-center gap-1 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2.5 py-0.5">
              <AlertCircle size={11} /> Not uploaded
            </span>
        }
      </div>

      {exists && version?.version != null && (
        <p className="text-xs text-gray-500">Live: <span className="font-medium text-gray-700">version {version.version}</span>{version.recordedAt && ` · recorded ${new Date(version.recordedAt).toLocaleDateString('en-GB')}`} · numbered automatically; each signup records the version it accepted.</p>
      )}
      {notice && (
        <p className="text-xs text-green-700 bg-green-50 border border-green-100 rounded px-3 py-2">{notice}</p>
      )}
      {error && (
        <p className="text-xs text-red-600 bg-red-50 border border-red-100 rounded px-3 py-2">{error}</p>
      )}

      <div className="flex items-center gap-2">
        {exists && (
          <a
            href={doc.viewUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 border border-indigo-200 rounded-lg px-3 py-1.5 hover:bg-indigo-50 transition-colors"
          >
            <ExternalLink size={13} /> View current
          </a>
        )}
        <Button
          size="sm"
          variant={exists ? 'outline' : 'default'}
          className="gap-1.5"
          onClick={() => fileRef.current?.click()}
          disabled={mutation.isPending}
        >
          <Upload size={13} />
          {mutation.isPending ? 'Uploading…' : exists ? 'Replace PDF' : 'Upload PDF'}
        </Button>
        <input
          ref={fileRef}
          type="file"
          accept="application/pdf,.pdf"
          className="hidden"
          onChange={e => {
            const file = e.target.files?.[0]
            if (file) mutation.mutate(file)
            e.target.value = ''
          }}
        />
      </div>
    </div>
  )
}

export default function LegalDocumentsPage() {
  const { data: status, isLoading } = useQuery({
    queryKey: ['legal-status'],
    queryFn: () => superadminApi.getLegalStatus().then(r => r.data),
  })

  const { data: versions } = useQuery({
    queryKey: ['legal-versions'],
    queryFn: () => superadminApi.getPlatformLegalVersions().then(r => r.data),
  })

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">Legal Documents</h1>
        <p className="text-sm text-gray-500 mt-1">
          Upload your Privacy Policy and Terms &amp; Conditions PDFs. These are shown to users during sign-up and linked in the app footer.
        </p>
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : (
        <div className="space-y-4 max-w-2xl">
          {DOCS.map(doc => (
            <DocCard key={doc.key} doc={doc} exists={status?.[doc.key] ?? false} version={versions?.find(v => v.key === doc.apiKey)} />
          ))}
        </div>
      )}

      <div className="mt-10 max-w-2xl">
        <h2 className="text-lg font-semibold text-gray-900">In-app documents</h2>
        <p className="text-sm text-gray-500 mt-1 mb-4">
          Written and edited here, shown on public pages and accepted by customers inside the app.
        </p>
        <div className="space-y-3">
          {IN_APP_DOCS.map(d => (
            <div key={d.key} className="bg-white border border-gray-200 rounded-xl p-5 flex items-center justify-between gap-4">
              <div>
                <p className="text-sm font-semibold text-gray-900">{d.label}</p>
                <p className="text-xs text-gray-500 mt-0.5">{d.description}</p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <a href={d.publicPath} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1.5 text-sm text-indigo-600 hover:text-indigo-800 border border-indigo-200 rounded-lg px-3 py-1.5 hover:bg-indigo-50 transition-colors">
                  <ExternalLink size={13} /> View
                </a>
                <Link to={`/superadmin/legal/${d.key}`} className="inline-flex items-center gap-1.5 text-sm text-white bg-indigo-600 hover:bg-indigo-700 rounded-lg px-3 py-1.5 transition-colors">
                  <Pencil size={13} /> Edit
                </Link>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
